import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { vertraegeApi, kategorienApi } from '../api';
import KiAnalyse from '../components/KiAnalyse';

const INITIAL_FORM = {
  titel: '', anbieter: '', kategorie_id: '',
  kosten: '', zahlungsintervall: 'monatlich',
  vertragsbeginn: '',
  vertragsende: '',
  vertragslaufzeit_monate: '',
  kuendigungsfrist_monate: '1',
  kuendigungsfrist_einheit: 'monate',
  auto_renewal: true,
  verlaengerungszeitraum_monate: '',
  notizen: '',
  status: 'aktiv',
  vertragsnummer: '',
  kundennummer: '',
};

const STATUS_LABELS = {
  aktiv: '✅ Aktiv',
  laeuft_bald_aus: '⏳ Läuft bald aus',
  kuendigungsfrist_laeuft: '🔔 Kündigungsfrist läuft',
  gekuendigt: '❌ Gekündigt',
  abgelaufen: '⚠️ Abgelaufen',
  verlaengert: '🔄 Verlängert',
  archiviert: '📦 Archiviert',
};

function formatDate(dateStr) {
  if (!dateStr) return '–';
  try {
    return new Date(`${dateStr}T00:00:00`).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch { return dateStr; }
}

export default function VertragFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [kategorien, setKategorien] = useState([]);
  const [kiText, setKiText] = useState('');
  const [form, setForm] = useState(INITIAL_FORM);
  const [berechnet, setBerechnet] = useState({});
  const [berechnung, setBerechnung] = useState(false);
  const debounceTimer = useRef(null);

  useEffect(() => {
    kategorienApi.list().then(res => setKategorien(Array.isArray(res) ? res : []));
    if (isEdit) {
      vertraegeApi.get(id).then(res => {
        const v = res.data;
        setForm({
          titel: v.titel || '',
          anbieter: v.anbieter || '',
          kategorie_id: v.kategorie_id || '',
          kosten: v.kosten || '',
          zahlungsintervall: v.zahlungsintervall || 'monatlich',
          vertragsbeginn: v.vertragsbeginn || '',
          vertragsende: v.vertragsende || '',
          vertragslaufzeit_monate: v.vertragslaufzeit_monate || '',
          kuendigungsfrist_monate: v.kuendigungsfrist_monate || '1',
          kuendigungsfrist_einheit: v.kuendigungsfrist_einheit || 'monate',
          auto_renewal: Boolean(v.auto_renewal),
          verlaengerungszeitraum_monate: v.verlaengerungszeitraum_monate || '',
          notizen: v.notizen || '',
          status: v.status || 'aktiv',
          vertragsnummer: v.vertragsnummer || '',
          kundennummer: v.kundennummer || '',
        });
        setBerechnet({
          vertragsende: v.vertragsende || null,
          naechste_kuendigung: v.naechste_kuendigung || null,
          letzte_kuendigung_moeglich: v.letzte_kuendigung_moeglich || null,
          naechste_verlaengerung: v.naechste_verlaengerung || null,
          erinnerung_1: v.erinnerung_1 || null,
          erinnerung_2: v.erinnerung_2 || null,
          erinnerung_3: v.erinnerung_3 || null,
          status_berechnet: v.status || null,
        });
        setLoading(false);
      });
    }
  }, [id, isEdit]);

  const triggerBerechnung = useCallback((formData) => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(async () => {
      if (!formData.vertragsbeginn && !formData.vertragsende && !formData.vertragslaufzeit_monate) return;
      setBerechnung(true);
      try {
        const res = await vertraegeApi.berechne(formData);
        setBerechnet(prev => ({ ...prev, ...res.data }));
      } catch {
        // Berechnung schlägt still fehl
      } finally {
        setBerechnung(false);
      }
    }, 600);
  }, []);

  const set = (key, value) => {
    setForm(prev => {
      const updated = { ...prev, [key]: value };
      if (['vertragsbeginn', 'vertragsende', 'vertragslaufzeit_monate',
           'kuendigungsfrist_monate', 'kuendigungsfrist_einheit',
           'auto_renewal', 'verlaengerungszeitraum_monate'].includes(key)) {
        triggerBerechnung(updated);
      }
      return updated;
    });
  };

  const handleKiUebernehmen = (daten) => {
    const intervallMap = {
      'jährlich': 'jaehrlich', 'vierteljährlich': 'vierteljaehrlich',
      'halbjährlich': 'halbjaehrlich', 'monatlich': 'monatlich', 'jaehrlich': 'jaehrlich',
    };
    const updates = {};
    if (daten.titel) updates.titel = daten.titel;
    if (daten.anbieter) updates.anbieter = daten.anbieter;
    if (daten.kosten) updates.kosten = daten.kosten;
    if (daten.zahlungsintervall) updates.zahlungsintervall = intervallMap[daten.zahlungsintervall.toLowerCase()] || 'monatlich';
    if (daten.vertragsbeginn) updates.vertragsbeginn = daten.vertragsbeginn;
    if (daten.vertragsende) updates.vertragsende = daten.vertragsende;
    if (daten.kuendigungsfrist_monate) updates.kuendigungsfrist_monate = daten.kuendigungsfrist_monate;
    if (daten.kundennummer && daten.kundennummer !== 'null') updates.notizen = 'Kundennummer: ' + daten.kundennummer;
    setForm(prev => {
      const merged = { ...prev, ...updates };
      triggerBerechnung(merged);
      return merged;
    });
    alert('✅ KI-Daten wurden ins Formular übernommen!');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.titel) return alert('Bitte Titel eingeben');
    setSaving(true);
    try {
      const data = {
        ...form,
        kosten: form.kosten ? parseFloat(form.kosten) : null,
        kuendigungsfrist_monate: parseInt(form.kuendigungsfrist_monate) || 1,
        vertragslaufzeit_monate: parseInt(form.vertragslaufzeit_monate) || null,
        verlaengerungszeitraum_monate: parseInt(form.verlaengerungszeitraum_monate) || null,
        kategorie_id: form.kategorie_id || null,
        vertragsbeginn: form.vertragsbeginn || null,
        vertragsende: form.vertragsende || null,
      };
      if (isEdit) {
        await vertraegeApi.update(id, data);
        navigate(`/vertraege/${id}`);
      } else {
        const res = await vertraegeApi.create(data);
        navigate(`/vertraege/${res.data.id}`);
      }
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.error || err.message));
    } finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding: 20, color: '#64748b', textAlign: 'center' }}>⏳ Laden...</div>;

  const showLaufzeitEnde = Boolean(form.vertragsbeginn && form.vertragslaufzeit_monate);
  const effectiveEnde = berechnet.vertragsende || form.vertragsende;
  const showDeadline = berechnet.letzte_kuendigung_moeglich || berechnet.naechste_kuendigung;

  return (
    <div style={{ padding: '16px', maxWidth: 680, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button onClick={() => navigate(-1)} className="btn-ghost">← Zurück</button>
        <h2 style={{ color: '#f1f5f9', fontSize: 18, fontWeight: 700 }}>
          {isEdit ? '✏️ Vertrag bearbeiten' : '➕ Neuer Vertrag'}
        </h2>
      </div>

      {/* KI-Analyse */}
      <Section label="🤖 KI-Analyse">
        <textarea
          value={kiText}
          onChange={e => setKiText(e.target.value)}
          placeholder="Vertragstext hier einfügen – KI extrahiert die Daten automatisch..."
          rows={3}
          style={{ ...inputStyle, resize: 'vertical', marginBottom: 8 }}
        />
        <KiAnalyse ocrText={kiText} onUebernehmen={handleKiUebernehmen} />
      </Section>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* ALLGEMEIN */}
        <Section label="📋 Allgemein">
          <FormGroup label="Bezeichnung *">
            <input value={form.titel} onChange={e => set('titel', e.target.value)}
              placeholder="z.B. Hausrat-Versicherung DEVK" required style={inputStyle} />
          </FormGroup>
          <TwoCol>
            <FormGroup label="Anbieter">
              <input value={form.anbieter} onChange={e => set('anbieter', e.target.value)}
                placeholder="z.B. DEVK, Telekom..." style={inputStyle} />
            </FormGroup>
            <FormGroup label="Kategorie">
              <select value={form.kategorie_id} onChange={e => set('kategorie_id', e.target.value)} style={inputStyle}>
                <option value="">– Kategorie wählen –</option>
                {(Array.isArray(kategorien) ? kategorien : []).map(k => (
                  <option key={k.id} value={k.id}>{k.icon} {k.name}</option>
                ))}
              </select>
            </FormGroup>
          </TwoCol>
        </Section>

        {/* LAUFZEIT & FRISTEN */}
        <Section label="📅 Laufzeit & Fristen">
          <TwoCol>
            <FormGroup label="Vertragsbeginn">
              <input type="date" value={form.vertragsbeginn}
                onChange={e => set('vertragsbeginn', e.target.value)} style={inputStyle} />
            </FormGroup>
            <FormGroup label="Laufzeit (Monate)" hint="→ berechnet Vertragsende">
              <input type="number" value={form.vertragslaufzeit_monate}
                onChange={e => set('vertragslaufzeit_monate', e.target.value)}
                placeholder="z.B. 24" min="1" max="360" style={inputStyle} />
            </FormGroup>
          </TwoCol>

          {showLaufzeitEnde ? (
            <ReadonlyField
              label="Vertragsende (aus Laufzeit)"
              value={formatDate(effectiveEnde)}
              loading={berechnung}
              highlight
            />
          ) : (
            <FormGroup label="Vertragsende (manuell)">
              <input type="date" value={form.vertragsende}
                onChange={e => set('vertragsende', e.target.value)} style={inputStyle} />
            </FormGroup>
          )}

          <TwoCol>
            <FormGroup label="Kündigungsfrist (Wert)">
              <input type="number" value={form.kuendigungsfrist_monate}
                onChange={e => set('kuendigungsfrist_monate', e.target.value)}
                min="0" max="120" style={inputStyle} />
            </FormGroup>
            <FormGroup label="Einheit">
              <select value={form.kuendigungsfrist_einheit}
                onChange={e => set('kuendigungsfrist_einheit', e.target.value)} style={inputStyle}>
                <option value="tage">Tage</option>
                <option value="wochen">Wochen</option>
                <option value="monate">Monate</option>
                <option value="jahre">Jahre</option>
              </select>
            </FormGroup>
          </TwoCol>

          <TwoCol>
            <FormGroup label="Auto-Verlängerung">
              <select value={form.auto_renewal ? 'ja' : 'nein'}
                onChange={e => set('auto_renewal', e.target.value === 'ja')} style={inputStyle}>
                <option value="ja">Ja</option>
                <option value="nein">Nein</option>
              </select>
            </FormGroup>
            {form.auto_renewal && (
              <FormGroup label="Verlängerungszeitraum (Monate)">
                <input type="number" value={form.verlaengerungszeitraum_monate}
                  onChange={e => set('verlaengerungszeitraum_monate', e.target.value)}
                  placeholder="z.B. 12" min="1" max="120" style={inputStyle} />
              </FormGroup>
            )}
          </TwoCol>
        </Section>

        {/* BERECHNETE FRISTEN */}
        {(showDeadline || berechnet.naechste_verlaengerung || berechnet.erinnerung_1) && (
          <Section label="🔔 Berechnete Fristen" hint="automatisch – nicht editierbar">
            {showDeadline && (
              <ReadonlyField
                label="Letzte Kündigung möglich"
                value={formatDate(berechnet.letzte_kuendigung_moeglich || berechnet.naechste_kuendigung)}
                loading={berechnung}
                highlight
              />
            )}
            {berechnet.naechste_verlaengerung && (
              <ReadonlyField
                label="Nächste Verlängerung"
                value={formatDate(berechnet.naechste_verlaengerung)}
                loading={berechnung}
              />
            )}
            {berechnet.erinnerung_1 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                <ReadonlyField label="Erinnerung 1 (90 Tage)" value={formatDate(berechnet.erinnerung_1)} loading={berechnung} small />
                <ReadonlyField label="Erinnerung 2 (30 Tage)" value={formatDate(berechnet.erinnerung_2)} loading={berechnung} small />
                <ReadonlyField label="Erinnerung 3 (7 Tage)" value={formatDate(berechnet.erinnerung_3)} loading={berechnung} small />
              </div>
            )}
            {berechnet.status_berechnet && (
              <ReadonlyField
                label="Berechneter Status"
                value={STATUS_LABELS[berechnet.status_berechnet] || berechnet.status_berechnet}
                loading={berechnung}
              />
            )}
          </Section>
        )}

        {/* KOSTEN & ZAHLUNG */}
        <Section label="💶 Kosten & Zahlung">
          <TwoCol>
            <FormGroup label="Kosten (€)">
              <input type="number" value={form.kosten} onChange={e => set('kosten', e.target.value)}
                placeholder="0.00" min="0" step="0.01" style={inputStyle} />
            </FormGroup>
            <FormGroup label="Zahlungsintervall">
              <select value={form.zahlungsintervall} onChange={e => set('zahlungsintervall', e.target.value)} style={inputStyle}>
                <option value="monatlich">Monatlich</option>
                <option value="vierteljaehrlich">Vierteljährlich</option>
                <option value="halbjaehrlich">Halbjährlich</option>
                <option value="jaehrlich">Jährlich</option>
                <option value="einmalig">Einmalig</option>
              </select>
            </FormGroup>
          </TwoCol>
        </Section>

        {/* STAMMDATEN */}
        <Section label="🔢 Stammdaten">
          <TwoCol>
            <FormGroup label="Vertragsnummer">
              <input value={form.vertragsnummer} onChange={e => set('vertragsnummer', e.target.value)}
                placeholder="z. B. V-2024-001" style={inputStyle} />
            </FormGroup>
            <FormGroup label="Kundennummer">
              <input value={form.kundennummer} onChange={e => set('kundennummer', e.target.value)}
                placeholder="z. B. KD-12345" style={inputStyle} />
            </FormGroup>
          </TwoCol>
        </Section>

        {/* NOTIZEN */}
        <Section label="📝 Notizen">
          <textarea value={form.notizen} onChange={e => set('notizen', e.target.value)}
            placeholder="Besonderheiten, Hinweise..."
            rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
        </Section>

        <button type="submit" disabled={saving} className="btn-primary" style={{
          cursor: saving ? 'not-allowed' : 'pointer', marginTop: 4,
          opacity: saving ? 0.5 : 1, minHeight: 44,
        }}>
          {saving ? '⏳ Speichern...' : (isEdit ? '💾 Änderungen speichern' : '✅ Vertrag anlegen')}
        </button>
      </form>
    </div>
  );
}

function Section({ label, hint, children }) {
  return (
    <div style={{ background: '#1e293b', borderRadius: 12, padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
        <h3 style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, margin: 0 }}>
          {label}
        </h3>
        {hint && <span style={{ color: '#475569', fontSize: 11 }}>{hint}</span>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>
    </div>
  );
}

function TwoCol({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>{children}</div>;
}

function FormGroup({ label, hint, children }) {
  return (
    <div>
      <label style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 5 }}>
        {label}
        {hint && <span style={{ color: '#475569', fontWeight: 400, marginLeft: 6, fontSize: 11 }}>{hint}</span>}
      </label>
      {children}
    </div>
  );
}

function ReadonlyField({ label, value, loading, highlight, small }) {
  return (
    <div>
      <label style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 5 }}>{label}</label>
      <div style={{
        background: '#0f172a', border: '1px solid #1e3a5f',
        color: highlight ? '#38bdf8' : '#64748b',
        padding: small ? '7px 10px' : '10px 12px', borderRadius: 8,
        fontSize: small ? 12 : 14, fontStyle: 'italic',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span>{loading ? <span style={{ opacity: 0.4 }}>wird berechnet…</span> : (value || '–')}</span>
        {!loading && value && value !== '–' && (
          <span style={{ fontSize: 10, color: '#334155' }}>automatisch</span>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%', background: '#0f172a', border: '1px solid #334155',
  color: '#f1f5f9', padding: '10px 12px', borderRadius: 8,
  fontSize: 14, outline: 'none', boxSizing: 'border-box',
};
